import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Store, ShoppingCart, BarChart3, Boxes, Users, ShieldCheck, ArrowRight, Check, Zap,
} from "lucide-react";

const FEATURES = [
  { icon: ShoppingCart, title: "Kasir Cepat (POS)", desc: "Proses transaksi dalam hitungan detik dengan antarmuka yang ringan dan responsif." },
  { icon: Boxes, title: "Manajemen Stok", desc: "Pantau stok real-time, dapatkan peringatan saat barang menipis." },
  { icon: BarChart3, title: "Laporan & Grafik", desc: "Analisa penjualan harian, produk terlaris, dan performa toko dengan grafik." },
  { icon: Users, title: "Data Pelanggan", desc: "Kelola pelanggan setia dan riwayat belanja mereka." },
  { icon: Store, title: "Multi-Toko", desc: "Setiap toko punya data terpisah dan aman, dikelola dalam satu akun." },
  { icon: ShieldCheck, title: "Peran & Izin", desc: "Atur akses Pemilik dan Kasir sesuai kebutuhan operasional Anda." },
];

const PLANS = [
  { name: "Basic", price: "Gratis", features: ["50 produk", "1 akun kasir", "Transaksi tanpa batas", "Laporan dasar"], highlight: false },
  { name: "Pro", price: "Rp99.000", period: "/bulan", features: ["500 produk", "5 akun kasir", "Laporan lengkap & grafik", "Manajemen pelanggan", "Ekspor data"], highlight: true },
  { name: "Premium", price: "Rp249.000", period: "/bulan", features: ["Produk tanpa batas", "Kasir tanpa batas", "Semua fitur Pro", "Prioritas dukungan", "Multi-cabang"], highlight: false },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center text-white">
              <Store className="w-5 h-5" />
            </div>
            <span className="font-heading font-extrabold text-xl tracking-tight">KASIRKU</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" data-testid="nav-login-btn">Masuk</Button></Link>
            <Link to="/register"><Button data-testid="nav-register-btn" className="rounded-lg">Daftar Gratis</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="inline-flex items-center gap-2 bg-accent text-accent-foreground text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
            <Zap className="w-3.5 h-3.5" /> Aplikasi Kasir untuk UMKM
          </span>
          <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            Kelola Toko Anda <span className="text-primary">Lebih Mudah</span> & Untung Lebih Banyak
          </h1>
          <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-lg">
            KASIRKU membantu pemilik UMKM mengelola produk, stok, transaksi, dan laporan penjualan dalam satu aplikasi yang cepat dan modern.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register">
              <Button size="lg" data-testid="hero-register-btn" className="rounded-lg text-base h-12 px-7 active:scale-95 transition-transform">
                Mulai Gratis <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" data-testid="hero-login-btn" className="rounded-lg text-base h-12 px-7">
                Sudah Punya Akun
              </Button>
            </Link>
          </div>
          <div className="mt-6 text-sm text-muted-foreground">Tanpa kartu kredit • Langsung pakai</div>
        </div>
        <div className="relative">
          <div className="absolute -inset-4 bg-primary/10 rounded-3xl rotate-3" />
          <img
            src="https://images.pexels.com/photos/36330752/pexels-photo-36330752.jpeg"
            alt="Pemilik toko UMKM"
            className="relative rounded-2xl border border-slate-200 shadow-lg w-full object-cover aspect-[4/3]"
          />
        </div>
      </section>

      {/* Features */}
      <section className="bg-white border-y border-slate-200 py-20">
        <div className="max-w-6xl mx-auto px-5">
          <div className="max-w-xl">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Semua yang Anda butuhkan untuk berjualan</h2>
            <p className="mt-3 text-muted-foreground">Fitur lengkap yang dirancang khusus untuk toko kecil dan menengah di Indonesia.</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <div key={i} className="p-6 rounded-xl border border-slate-200 hover:shadow-md hover:-translate-y-1 transition-all duration-200 bg-white">
                <div className="w-11 h-11 rounded-lg bg-accent text-primary flex items-center justify-center">
                  <f.icon className="w-5 h-5" />
                </div>
                <h3 className="mt-4 font-heading font-bold text-lg">{f.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 max-w-6xl mx-auto px-5">
        <div className="text-center max-w-xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Paket harga yang sederhana</h2>
          <p className="mt-3 text-muted-foreground">Pilih paket yang sesuai dengan skala bisnis Anda. Upgrade kapan saja.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6 items-start">
          {PLANS.map((p) => (
            <div
              key={p.name}
              className={`p-7 rounded-2xl border bg-white ${p.highlight ? "border-primary shadow-lg ring-2 ring-primary/20 md:-translate-y-3" : "border-slate-200"}`}
            >
              {p.highlight && <div className="text-xs font-bold uppercase tracking-wider text-primary mb-2">Paling Populer</div>}
              <h3 className="font-heading font-bold text-xl">{p.name}</h3>
              <div className="mt-3 flex items-end gap-1">
                <span className="font-heading text-3xl font-extrabold">{p.price}</span>
                {p.period && <span className="text-muted-foreground text-sm mb-1">{p.period}</span>}
              </div>
              <ul className="mt-6 space-y-3">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-emerald-500 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Link to="/register" className="block mt-7">
                <Button className="w-full rounded-lg" variant={p.highlight ? "default" : "outline"} data-testid={`plan-${p.name.toLowerCase()}-btn`}>
                  Pilih {p.name}
                </Button>
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-5 pb-20">
        <div className="rounded-3xl bg-slate-900 text-white p-10 sm:p-14 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Siap membuat toko Anda lebih rapi?</h2>
          <p className="mt-3 text-slate-300 max-w-lg mx-auto">Bergabung dengan ribuan pemilik UMKM yang mengelola tokonya dengan KASIRKU.</p>
          <Link to="/register">
            <Button size="lg" className="mt-8 rounded-lg h-12 px-8 text-base active:scale-95 transition-transform" data-testid="cta-register-btn">
              Daftar Sekarang, Gratis
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-8 text-center text-sm text-muted-foreground">
        © 2026 KASIRKU — Aplikasi kasir untuk UMKM Indonesia.
      </footer>
    </div>
  );
}
